import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import type {
  CommunityFeedItemV2Dto,
  CommunityFeedStatsDto,
  CommunityGroupV2Dto,
  MessageLabelDto,
  SelfDto,
} from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { CommunityMessage } from "@/components/CommunityFeed/CommunityMessage";
import { CommunityPoll } from "@/components/CommunityFeed/CommunityPoll";
import { CommunitySurvey } from "@/components/CommunityFeed/CommunitySurvey";
import { ResidentCreatePostModal } from "@/components/CommunityFeed/ResidentCreatePostModal";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ArrowNarrowUpIcon } from "@/components/Icons/ArrowNarrowUpIcon";
import { Building02Icon } from "@/components/Icons/Building02Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { MessageQuestionSquareIcon } from "@/components/Icons/MessageQuestionSquareIcon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { CommunityFeedOperationalAnalyticsSummary } from "@/components/OperationalAnalytics/OperationalAnalyticsSummaries";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Button } from "@/design-system/Button";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { useScrollToPageTop } from "@/hooks/useScrollToPageTop";
import { useSignalRHub, useSignalRInvocation, useSignalRSubscription } from "@/hooks/useSignalR";
import { useSlug } from "@/hooks/useSlug";
import { canCreateMessage, canPostInMarketplace } from "@/modules/community-feed/permissions";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import type { CommunityFeedFilterValues } from "../../components/CommunityFeedFilterDrawer";
import { CommunityFeedFilterDrawer } from "../../components/CommunityFeedFilterDrawer";
import type { ALL_COMMUNITY_FEED_TABS } from "../../constants";

export interface CommunityFeedProps {
  sessionUser: SelfDto;
  filterOptions: {
    groups: CommunityGroupV2Dto[];
    helpCategories: CommunityGroupV2Dto[];
  };
  isLoadingGroups: boolean;
  unreadCounts: CommunityFeedStatsDto | undefined;
  counts: Record<(typeof ALL_COMMUNITY_FEED_TABS)[number], number>;
  selectedTab: (typeof ALL_COMMUNITY_FEED_TABS)[number] | undefined;
  onChangeTab: (tab: (typeof ALL_COMMUNITY_FEED_TABS)[number]) => void;
  messages: CommunityFeedItemV2Dto[];
  hasMoreMessages: boolean | undefined;
  fetchMoreMessages: () => Promise<unknown>;
  refetchMessages: () => void;
  isLoadingMessages: boolean;
  isLoadingMoreMessages: boolean;
  search: string;
  onChangeSearch: (value: string) => void;
  filter: CommunityFeedFilterValues;
  onUpdateFilter: (filter: CommunityFeedFilterValues) => void;
}
export function CommunityFeed({
  search,
  onChangeSearch,
  sessionUser,
  filter,
  filterOptions,
  unreadCounts,
  messages,
  hasMoreMessages,
  counts,
  selectedTab,
  onUpdateFilter,
  onChangeTab,
  fetchMoreMessages,
  refetchMessages,
  isLoadingMessages,
  isLoadingMoreMessages,
}: CommunityFeedProps): React.ReactNode {
  const postHog = usePostHog();
  const slug = useSlug();
  const hasPermission = usePermission();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollToTop = useScrollToPageTop();
  const { state: locationState }: { state?: MessageRouteStates["list"] } = useLocation();
  const [residentPostType, setResidentPostType] = useState<MessageLabelDto | null>(
    locationState?.residentCreatePostType || null,
  );
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreMessages && hasMoreMessages) {
        void fetchMoreMessages();
      }
    }, [fetchMoreMessages, hasMoreMessages, isLoadingMoreMessages]),
  });

  const [isFilterOpened, filterHandler] = useBool();
  const [isNewItemAvailable, newItemAvailabilityHandlers] = useBool(false);
  const [isNewVideoProcessed, newVideoProcessedHandlers] = useBool(false);
  const [isContentNotFoundModalOpen, contentNotFoundModalHandlers] = useBool(!!locationState?.contentNotFoundReason);
  const projectId = useProjectId();

  const hubConnectionQuery = {
    query: `userId=${sessionUser.id}`,
  };
  const { signalRConnection: communityFeedHubConnection } = useSignalRHub("community-feed-hub", hubConnectionQuery);
  const { signalRConnection: notificationHubConnection } = useSignalRHub("notification-hub", hubConnectionQuery);
  const { invoke: onJoinGroup } = useSignalRInvocation(communityFeedHubConnection, "JoinGroup");

  const onNewItemOnFeed = useCallback(
    (
      ...args: [
        {
          entityId: string;
          entityType: "message" | "comment" | "poll" | "survey";
          authorId: string;
        },
      ]
    ) => {
      function isFiltersEmpty(): boolean {
        return !search && !filter.startDate && !filter.endDate && (!filter.groupIds || filter.groupIds?.length === 0);
      }
      if (args[0].authorId === sessionUser.id) {
        return;
      }
      if ((selectedTab === "myFeed" || selectedTab === "all") && isFiltersEmpty()) {
        newItemAvailabilityHandlers.setTrue();
      }
    },
    [
      sessionUser.id,
      selectedTab,
      search,
      filter.startDate,
      filter.endDate,
      filter.groupIds,
      newItemAvailabilityHandlers,
    ],
  );

  const onNewVideoProcessed = useCallback(
    (
      ...args: [
        {
          authorId: string;
        },
      ]
    ) => {
      if (args[0].authorId !== sessionUser.id) {
        return;
      }
      newVideoProcessedHandlers.setTrue();
    },
    [newVideoProcessedHandlers, sessionUser.id],
  );

  useEffect(() => {
    if (locationState?.residentCreatePostType) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setResidentPostType(locationState?.residentCreatePostType);
    }
  }, [locationState?.residentCreatePostType]);

  useSignalRSubscription(communityFeedHubConnection, SIGNALR_EVENTS.COMMUNITY_FEED.NEW_POST, onNewItemOnFeed);
  useSignalRSubscription(
    notificationHubConnection,
    SIGNALR_EVENTS.COMMUNITY_FEED.NEW_VIDEO_PROCESSED,
    onNewVideoProcessed,
  );
  useKey("Escape", filterHandler.setFalse, isFilterOpened);

  const handleRefreshFeed = () => {
    refetchMessages();
    scrollToTop();
  };

  const residentCreatePostMenuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("page.message-feed.resident-create-post.create-options.inform"),
      },
      value: "inform",
      icon: <Edit05Icon />,
      callbackFn: () => {
        postHog.capture("clicked_inform");
        setResidentPostType("inform");
      },
      isHidden: !hasPermission(canCreateMessage),
    },
    {
      labels: {
        default: t("page.message-feed.resident-create-post.create-options.help"),
      },
      value: "ask-help",
      icon: <MessageQuestionSquareIcon />,
      callbackFn: () => {
        postHog.capture("clicked_ask_help");
        setResidentPostType("askHelp");
      },
      isHidden: !hasPermission(canCreateMessage),
    },
    {
      labels: {
        default: t("page.message-feed.resident-create-post.create-options.marketplace"),
      },
      value: "marketplace",
      icon: <Building02Icon />,
      callbackFn: () => {
        postHog.capture("clicked_marketplace");
        setResidentPostType("sellItem");
      },
      isHidden: !hasPermission(canPostInMarketplace),
    },
  ];
  const tabItems = useMemo(() => {
    let tabs: TabItem<(typeof ALL_COMMUNITY_FEED_TABS)[number]>[];
    if (sessionUser.isAdmin) {
      tabs = [
        {
          value: "all",
          label: t("page.message-feed.tabs.all"),
          count: unreadCounts?.allCount,
          "data-testid": "all-messages-tab-btn",
        },
        {
          value: "announcements",
          label: t("page.message-feed.tabs.announcements"),
          count: unreadCounts?.announcementsCount,
          "data-testid": "announcements-tab-btn",
        },
        {
          value: "question",
          label: t("page.message-feed.tabs.questions"),
          "data-testid": "questions-tab-btn",
        },
        {
          label: t("page.message-feed.tabs.mine"),
          value: "own",
          count: unreadCounts?.myPostsCount,
          "data-testid": "my-messages-tab-btn",
        },
        {
          label: t("page.message-feed.tabs.scheduled"),
          value: "scheduled",
          count: counts.scheduled,
          "data-testid": "scheduled-tab-btn",
        },
      ];

      if (sessionUser.isSuperAdmin) {
        tabs.push({
          label: t("page.message-feed.tabs.deleted"),
          value: "deleted",
          "data-testid": "deleted-tab-btn",
        });
      }
    } else {
      tabs = [
        {
          value: "myFeed",
          label: t("page.message-feed.tabs.my-feed"),
          count: unreadCounts?.allCount,
          "data-testid": "my-feed-tab-btn",
        },
        {
          value: "question",
          label: t("page.message-feed.tabs.questions"),
          "data-testid": "questions-tab-btn",
        },
        {
          value: "announcements",
          label: t("page.message-feed.tabs.announcements"),
          count: unreadCounts?.announcementsCount,
          "data-testid": "announcements-tab-btn",
        },
        {
          value: "all",
          label: t("page.message-feed.tabs.all"),
          count: undefined,
          "data-testid": "all-messages-tab-btn",
        },
      ];
    }

    return tabs;
  }, [
    sessionUser.isSuperAdmin,
    sessionUser.isAdmin,
    unreadCounts?.allCount,
    unreadCounts?.announcementsCount,
    unreadCounts?.myPostsCount,
    counts,
    t,
  ]);

  const handleChangeTab = (tab: (typeof ALL_COMMUNITY_FEED_TABS)[number]) => {
    postHog.capture("clicked_change_message_feed_tab", {
      tab,
    });

    onChangeTab(tab);
  };

  const handleCloseContentNotFoundModal = () => {
    contentNotFoundModalHandlers.setFalse();

    // Clear the state to prevent showing the modal again on back navigation
    history.replaceState({}, "");
  };

  const amountActiveFilters = Object.entries(filter).filter(([filterKey, filterValue]) => {
    if (filterValue === undefined) return false;

    if (filterKey === "startDate" || filterKey === "endDate" || filterKey === "groupIds") {
      if (Array.isArray(filterValue)) {
        return filterValue.length > 0;
      }

      if (typeof filterValue === "string") {
        return Boolean(filterValue);
      }
    }

    return false;
  }).length;

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.message-feed.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.message-feed.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{
                scope: "project",
                entity: "community-feed",
                page: "list",
                filters: {
                  tab: selectedTab || "all",
                  startDate: filter.startDate,
                  endDate: filter.endDate,
                  groupIds: filter.groupIds,
                  search,
                },
              }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.messages.suggestion.1"),
                t("component.ask-nobi-chat-drawer.messages.suggestion.2"),
              ]}
            />
            {hasPermission(canCreateMessage) && sessionUser.isAdmin && (
              <>
                {/* Desktop */}
                <span className="hidden @md:flex">
                  <Button
                    type="link"
                    href={routes.communityFeed.adminCreatePost({
                      slug,
                    })}
                  >
                    {t("page.message-feed.button.create.post")}
                  </Button>
                </span>
                <span className="@md:hidden">
                  <IconButton
                    variant="filled"
                    title={t("page.message-feed.button.create.post")}
                    type="link"
                    href={routes.communityFeed.adminCreatePost({
                      slug,
                    })}
                    icon={<PlusIcon />}
                    isCircular
                  />
                </span>
              </>
            )}
            {hasPermission(canCreateMessage) && !sessionUser.isAdmin && (
              <>
                <span className="hidden @md:block">
                  <ContextMenu
                    items={residentCreatePostMenuActions}
                    triggerComponent={
                      <Button data-testid="create-message">{t("page.message-feed.button.create.post")}</Button>
                    }
                  />
                </span>
                <span className="@md:hidden">
                  <ContextMenu
                    items={residentCreatePostMenuActions}
                    triggerComponent={
                      <IconButton
                        variant="filled"
                        title={t("page.message-feed.button.create.post")}
                        icon={<PlusIcon />}
                        withTooltip={false}
                        isCircular
                      />
                    }
                  />
                </span>
              </>
            )}
          </>
        }
        header={
          <Inline gap="sm" align="center">
            <SearchInput
              width="wide"
              defaultValue={search}
              placeholder={t("page.message-feed.search.placeholder")}
              onChange={(e) => onChangeSearch(e.target.value)}
              onFocusCapture={() => {
                postHog.capture("clicked_feed_search_field");
              }}
            />
            <Button icon={<FilterFunnel01Icon />} variant="soft" onClick={filterHandler.toggle}>
              <Inline as="span" gap="sm" align="center" wrap={false}>
                {t("common.action.filter")}
                {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
              </Inline>
            </Button>
          </Inline>
        }
      >
        <Stack gap="md">
          <CommunityFeedOperationalAnalyticsSummary />
          {selectedTab && (
            <Tabs.Root<(typeof ALL_COMMUNITY_FEED_TABS)[number]>
              tabs={tabItems}
              onSelectTab={handleChangeTab}
              activeTab={selectedTab}
            >
              {tabItems.map((tabItem) => {
                return (
                  <Tabs.Content value={tabItem.value} key={tabItem.value} isLoading={isLoadingMessages}>
                    <section className="relative max-w-content-standard">
                      <AnimatePresence>
                        {/* Floating update message */}
                        {(isNewItemAvailable || isNewVideoProcessed) && (
                          <aside className="sticky top-0 z-50 mx-auto w-fit translate-y-2">
                            {isNewItemAvailable && !isNewVideoProcessed && (
                              <Button
                                onClick={() => {
                                  newItemAvailabilityHandlers.setFalse();
                                  handleRefreshFeed();
                                }}
                                icon={<ArrowNarrowUpIcon />}
                                isCircular
                              >
                                {t("page.message-feed.button.new-messages")}
                              </Button>
                            )}
                            {isNewVideoProcessed && (
                              <Button
                                onClick={() => {
                                  newVideoProcessedHandlers.setFalse();
                                  handleRefreshFeed();
                                }}
                                icon={<ArrowNarrowUpIcon />}
                                isCircular
                              >
                                {t("page.message-feed.button.video-processed")}
                              </Button>
                            )}
                          </aside>
                        )}
                        {/* Empty state for scheduled posts tab */}
                        {messages.length === 0 && selectedTab === "scheduled" && (
                          <SectionCard>
                            <Stack gap="md" align="center">
                              <Edit05Icon size={24} />
                              <header className="text-center">
                                <h3 className="text-heading-3">{t("page.message-feed.tab.scheduled.empty.title")}</h3>
                                <p>{t("page.message-feed.tab.scheduled.empty.description")}</p>
                              </header>
                              <Button
                                type="link"
                                onClick={() => {
                                  postHog.capture("clicked_plan_schedule_post");
                                }}
                                href={routes.communityFeed.adminCreatePost({
                                  slug,
                                })}
                              >
                                {t("page.message-feed.button.create.post")}
                              </Button>
                            </Stack>
                          </SectionCard>
                        )}
                        {messages.length === 0 && selectedTab !== "scheduled" && (
                          <p className="text-caption">{t("common.label.no-result")}</p>
                        )}
                        {messages.length > 0 && (
                          <CommunityPostViewCountTrackingProvider>
                            <ul
                              className={twJoin(
                                "relative flex flex-col [&>li:not(:first-child)]:pt-space-md",
                                (isNewItemAvailable || isNewVideoProcessed) && "-mt-10",
                              )}
                            >
                              {messages.map((message) => {
                                return (
                                  <Fragment
                                    key={message.type + (message.message?.id || message.poll?.id || message.survey?.id)}
                                  >
                                    {message.type === "message" && message.message && (
                                      <motion.li
                                        variants={{
                                          hidden: { opacity: 0, scale: 0.98 },
                                          animate: { opacity: 1, scale: 1 },
                                        }}
                                        initial={
                                          locationState?.newMessageId === message.message.id ? "hidden" : "animate"
                                        }
                                        animate="animate"
                                        transition={{
                                          type: "tween",
                                          ease: "easeInOut",
                                          duration: 0.4,
                                        }}
                                        onAnimationComplete={() => {
                                          window.history.replaceState({}, "");
                                        }}
                                        key={message.message.id}
                                        data-testid="community-post"
                                      >
                                        <CommunityMessage message={message.message} onJoinGroup={onJoinGroup} />
                                      </motion.li>
                                    )}
                                    {message.type === "poll" && message.poll && (
                                      <li data-testid="community-poll">
                                        <CommunityPoll poll={message.poll} />
                                      </li>
                                    )}
                                    {message.type === "survey" && message.survey && (
                                      <li data-testid="community-survey">
                                        <CommunitySurvey survey={message.survey} />
                                      </li>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </ul>
                          </CommunityPostViewCountTrackingProvider>
                        )}
                        {hasMoreMessages && <CollectionLoadingState key={`loader-${tabItem.value}`} ref={ref} />}
                      </AnimatePresence>
                    </section>
                  </Tabs.Content>
                );
              })}
            </Tabs.Root>
          )}
        </Stack>
      </DocumentPage>
      <CommunityFeedFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterHandler.set}
        filter={filter}
        filterOptions={filterOptions}
        onUpdate={onUpdateFilter}
      />

      <ResidentCreatePostModal
        isOpened={!!residentPostType}
        onOpenChange={async (state) => {
          if (!state) {
            await navigate(location.pathname, {
              state: {
                ...locationState,
                residentCreatePostType: null,
              } satisfies MessageRouteStates["list"],
            });
            setResidentPostType(null);
          }
        }}
        type={residentPostType}
        groupId={locationState?.groupId}
        isGeneral={locationState?.isGeneral || false}
      />
      <ConfirmModal
        tone="primary"
        title={t("page.message-feed.content-not-found.modal.title")}
        description={t("page.message-feed.content-not-found.modal.description")}
        isLoading={false}
        onOpenChange={handleCloseContentNotFoundModal}
        onReject={handleCloseContentNotFoundModal}
        rejectBtnProps={{ hide: true }}
        onResolve={handleCloseContentNotFoundModal}
        resolveBtnProps={{
          text: t("common.action.ok"),
        }}
        isOpened={isContentNotFoundModalOpen}
        shouldCloseOnEsc
        data-testid="content-not-found-modal"
      />
    </>
  );
}
